import React, { useMemo } from 'react';
import { IS_T3_WEBSITE } from '../../constants/environment';
import LightBox from '../LightBox';
import VehicleInfo from '../VehicleInfo';
import VehicleStatusInfo from '../VehicleStatusInfo';
import {
  VehicleHeaderWrapper,
  HeadingContainerNonAudi,
  HeaderTextContainer,
  BackgroundImage,
  ImageMobileUsed,
  TabletSubHeader,
  HeaderUsed,
  Image,
} from './VehicleHeader.style';
import type { VehicleInfo as VehicleInfoTypes, Impel360ViewCTA } from '../../types/index';
import useOptimizePrimaryImage from '../../hooks/useOptimizePrimaryImage';
import usePalette from '../../hooks/usePalette';
import usePrimaryImageUrl from '../../hooks/usePrimaryImageUrl';
import isFallback from '../../utils/isFallback';
import Impel360CTA from '../Impel360CTA';

interface HeaderTier3Props {
  vehicleData?: VehicleInfoTypes;
  cta360View: Impel360ViewCTA;
  isTablet: boolean;
  isMobile: boolean;
  loading: boolean;
}

const HeaderTier3Wrapper: React.FC<HeaderTier3Props> = ({
  vehicleData,
  cta360View,
  isTablet,
  isMobile,
}) => {
  let primaryImageUrl: string = usePrimaryImageUrl(vehicleData);

  const { backgroundColor, darkenedBackgroundColor, textColor } = usePalette(vehicleData);
  const vehicleType = vehicleData?.vehicleType;
  const modelName = vehicleData?.modelName;
  const nonAudi = vehicleType === 'non-audi';
  const isUsed = vehicleType === 'used';
  const isNew = vehicleType === 'new';

  primaryImageUrl = useOptimizePrimaryImage(primaryImageUrl, isNew);
  const fallback = primaryImageUrl ? isFallback(primaryImageUrl) : false;

  const hasDealerImages = !!vehicleData?.vehicleDealerImages;

  const images = vehicleData?.vehicleMedia.mediaImages;
  const dealerImages = vehicleData?.vehicleDealerImages?.map((image, index) => ({
    url: image,
    imageType: index.toString(),
    config: index.toString(),
  }));

  const { is360ViewEnabled, cta360ViewButtonLabel } = cta360View;

  const imagesToRender = useMemo(() => {
    if (IS_T3_WEBSITE && dealerImages) return dealerImages;
    return images;
  }, [dealerImages, images]);

  const dealerPrimaryImage = dealerImages?.[0].url;

  const mainImage = useMemo(() => {
    if (IS_T3_WEBSITE && dealerPrimaryImage) return dealerPrimaryImage;
    return primaryImageUrl;
  }, [dealerPrimaryImage, primaryImageUrl]);

  return (
    <>
      <HeaderUsed
        color={nonAudi ? '#ffffff' : textColor}
        fallback={fallback}
        backgroundColor={nonAudi ? '#666666' : backgroundColor}
        darkenedBackgroundColor={darkenedBackgroundColor}
        nonAudi={nonAudi}
        mediaImages={vehicleData?.vehicleImages ?? ['']}
        data-testid="header"
      >
        <VehicleHeaderWrapper isUsed={isUsed || hasDealerImages} fallback={fallback}>
          <HeadingContainerNonAudi backgroundColor={nonAudi ? '#666666' : backgroundColor}>
            <HeaderTextContainer
              nonAudi={nonAudi}
              isUsed={isUsed || hasDealerImages}
              fallback={fallback}
              hasDealerImages={hasDealerImages}
              data-testid="vehicle-info"
            >
              <div
                data-vehicle-status={vehicleData?.vehicleOrderStatus || 'Unavailable'}
                data-vehicle-sale-type={
                  vehicleData?.saleOrderType || vehicleData?.vehicleInventoryType
                }
              >
                <VehicleInfo />

                {IS_T3_WEBSITE && <VehicleStatusInfo />}
                <Impel360CTA
                  isVisible={is360ViewEnabled}
                  label={cta360ViewButtonLabel}
                  color={textColor}
                  isNew={isNew}
                  isT3={IS_T3_WEBSITE}
                />
              </div>
            </HeaderTextContainer>
            {!isTablet && !isMobile && mainImage && (
              <BackgroundImage
                image={mainImage}
                nonAudi={nonAudi}
                fallback={fallback}
                data-testid="primary-image"
                hasDealerImages={hasDealerImages}
              />
            )}
            {isMobile && mainImage && (
              <ImageMobileUsed src={mainImage} data-testid="primary-image" alt={modelName} />
            )}
          </HeadingContainerNonAudi>
        </VehicleHeaderWrapper>

        {!isTablet && (
          <LightBox
            usedImages={vehicleData?.vehicleDealerImages}
            images={imagesToRender}
            header={mainImage}
            modelName={modelName}
            nonAudi={nonAudi}
            used={isUsed || hasDealerImages}
            hasDealerImages={hasDealerImages}
          />
        )}
      </HeaderUsed>
      {isTablet && (
        <TabletSubHeader>
          {mainImage && <Image src={mainImage} alt={modelName} />}
          <LightBox
            usedImages={vehicleData?.vehicleDealerImages}
            images={imagesToRender}
            header={mainImage}
            modelName={modelName}
            nonAudi={nonAudi}
            used={isUsed || hasDealerImages}
            hasDealerImages={hasDealerImages}
          />
        </TabletSubHeader>
      )}
    </>
  );
};

export default HeaderTier3Wrapper;
